/**
 * XLSX Export Utility
 * Converts an array of objects to a downloadable Excel (.xlsx) file using SheetJS.
 */
import * as XLSX from 'xlsx';

/**
 * Export an array of objects to an XLSX download.
 * @param data - array of records to export
 * @param filename - download filename (without extension is fine)
 * @param columns - optional: explicit column ordering and widths. If omitted, keys from the first row are used.
 * @param sheetName - name of the worksheet tab (default: 'Report')
 */
export function exportToXlsx<T extends Record<string, unknown>>(
  data: T[],
  filename: string,
  columns?: { key: keyof T; label: string; width?: number }[],
  sheetName: string = 'Report'
): void {
  if (data.length === 0) {
    alert('No data to export.');
    return;
  }

  const cols: { key: keyof T; label: string; width?: number }[] = columns ?? Object.keys(data[0]).map((k) => ({ key: k as keyof T, label: k }));
  const headers = cols.map((c) => c.label);
  const rows = data.map((item) => cols.map((c) => item[c.key] as string | number | null | undefined));

  const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);

  // Set column widths
  ws['!cols'] = cols.map((c) => ({ wch: c.width || Math.max(c.label.length, 12) }));

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  XLSX.writeFile(wb, filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`);
}
